// StaffChat.jsx (patched)
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaEllipsisV,
  FaPaperclip,
  FaSmile,
  FaMicrophone,
  FaCheck,
  FaCheckDouble,
  FaFileDownload,
  FaChevronLeft,
  FaChevronRight,
  FaFileAlt,
  FaTimes,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { MdSend } from "react-icons/md";
import "./chat.css";
import { FaBurger } from "react-icons/fa6";
import { FiMenu } from "react-icons/fi";

const StaffChat = React.forwardRef((props, ref) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [currentUser, setCurrentUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // UI toggles
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // edit / menu state
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(null); // message id that has menu open
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const pollingRef = useRef({
    convInterval: null,
    messagesInterval: null,
    typingInterval: null,
  });
  const messageTrackerRef = useRef(new Set());
  const lastUnreadRef = useRef(null);
  const initialLoadRef = useRef(true);
  const typingTimeoutRef = useRef(null);
  const typingSentRef = useRef(false);
  // store logos in a map by cso_id
  const [csoLogos, setCsoLogos] = useState({});
  // Add responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Modified toggle function that works for both desktop and mobile
  const toggleSidebar = () => {
    if (isMobile) {
      // On mobile, we want to completely show/hide the sidebar
      setSidebarCollapsed((prev) => !prev);
    } else {
      // On desktop, we want the mini sidebar mode
      setSidebarCollapsed((prev) => !prev);
    }
  };
  const getUserId = (u) => u?.id ?? u?.userId ?? null;

  const sortMessagesAscending = (arr) => {
    if (!Array.isArray(arr)) return arr;
    return arr
      .slice()
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  // small threshold in px to consider "at bottom"
  const isUserAtBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return (
      container.scrollHeight - container.clientHeight - container.scrollTop < 40
    );
  };

  const scrollToBottom = (behavior = "smooth") => {
    const container = messagesContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior });
      return;
    }
    if (behavior === "auto") container.scrollTop = container.scrollHeight;
    else messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Preserve user's scroll when messages change.
  const updateMessagesPreserveScroll = (updater, forceBottom = false) => {
    const container = messagesContainerRef.current;
    if (!container) {
      setMessages((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
      return;
    }

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    const wasAtBottom =
      prevScrollHeight - container.clientHeight - prevScrollTop < 40;

    setMessages((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );

    // adjust after DOM updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!container) return;
        if (wasAtBottom || forceBottom) {
          container.scrollTop = container.scrollHeight;
        } else {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = Math.max(
            0,
            prevScrollTop + (newScrollHeight - prevScrollHeight)
          );
        }
      }, 0);
    });
  };

  // sync filteredConversations with searchTerm
  const syncFiltered = (convs) => {
    if (!searchTerm) setFilteredConversations(convs);
    else {
      const term = searchTerm.toLowerCase();
      setFilteredConversations(
        convs.filter(
          (c) =>
            (c.csoName || "").toLowerCase().includes(term) ||
            (c.last_message || "").toLowerCase().includes(term)
        )
      );
    }
  };

  // auto-resize textarea (Telegram-like)
  const autoResize = () => {
    const ta = messageInputRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const newHeight = Math.min(ta.scrollHeight, 200); // cap height
    ta.style.height = `${newHeight}px`;
  };

  // call autoResize when editing message starts or newMessage changes
  useEffect(() => {
    // keep input sized for content
    autoResize();
  }, [newMessage]);

  useEffect(() => {
    // when entering edit mode, focus and size input
    if (editingMessageId) {
      setTimeout(() => {
        messageInputRef.current?.focus();
        autoResize();
      }, 30);
    } else {
      // reset height when not editing
      if (messageInputRef.current) messageInputRef.current.style.height = "";
    }
  }, [editingMessageId]);

  // ----- fetch current user -----
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/users/me`, {
          withCredentials: true,
        });
        if (data) setCurrentUser(data);
      } catch (err) {
        console.error("Error fetching user", err);
      }
    };
    fetchCurrentUser();
  }, [API_URL]);

  // ----- conversations polling -----
  const fetchActiveCSOs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chat/active-csos`, {
        withCredentials: true,
      });
      if (!Array.isArray(data)) return;

      const unreadMap = {};
      data.forEach((cso) => {
        unreadMap[cso.cso_id] = cso.unread_count;
      });
      setUnreadCounts(unreadMap);

      // update conversations and filtered view
      setConversations(data);
      syncFiltered(data);

      // cache any logo URLs returned by the API
      const logoUpdates = {};
      data.forEach((cso) => {
        if (cso.logo) logoUpdates[cso.cso_id] = cso.logo;
      });
      if (Object.keys(logoUpdates).length)
        setCsoLogos((prev) => ({ ...prev, ...logoUpdates }));

      // fetch missing logos (only for new/missing ones) in background
      const missing = data
        .filter((cso) => !cso.logo && !csoLogos[cso.cso_id])
        .map((cso) => cso.cso_id);
      if (missing.length > 0) {
        const fetches = missing.map((id) =>
          axios
            .get(`${API_URL}/api/cso/${id}`, { withCredentials: true })
            .then((r) => ({ id, logo: r.data?.logo }))
            .catch(() => null)
        );
        Promise.allSettled(fetches)
          .then((results) => {
            const updates = {};
            results.forEach((r) => {
              if (r.status === "fulfilled" && r.value && r.value.logo)
                updates[r.value.id] = r.value.logo;
            });
            if (Object.keys(updates).length)
              setCsoLogos((prev) => ({ ...prev, ...updates }));
          })
          .catch(() => {});
      }
    } catch (err) {
      console.error("Error fetching active CSOs:", err);
    }
  }, [API_URL, csoLogos, searchTerm]);

  useEffect(() => {
    if (!getUserId(currentUser)) return;
    fetchActiveCSOs();
    pollingRef.current.convInterval = setInterval(fetchActiveCSOs, 5000);
    return () => {
      if (pollingRef.current.convInterval)
        clearInterval(pollingRef.current.convInterval);
    };
  }, [currentUser, fetchActiveCSOs]);

  // ----- filter conversations -----
  useEffect(() => {
    syncFiltered(conversations);
  }, [searchTerm, conversations]);

  // ----- fetch messages once (full history) & mark read for staff -----
  const fetchMessagesOnce = useCallback(
    async (csoId) => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chat/cso/${csoId}`, {
          withCredentials: true,
        });
        if (!Array.isArray(data)) return;

        const ordered = sortMessagesAscending(data);
        messageTrackerRef.current = new Set(ordered.map((m) => m.id));

        // Initial load: set messages directly
        setMessages(ordered);

        const firstUnread = ordered.find(
          (m) => m.sender_type === "cso" && !(m.read_at || m.is_read)
        );
        lastUnreadRef.current = firstUnread ? firstUnread.id : null;

        try {
          const userId = getUserId(currentUser);
          await axios
            .post(
              `${API_URL}/api/chat/mark-read`,
              { csoId, actorType: "staff", actorId: userId },
              { withCredentials: true }
            )
            .catch(() => {});
        } catch (e) {}

        initialLoadRef.current = true;

        // fetch and cache logo for this cso (so UI can render it immediately)
        try {
          const res = await axios.get(`${API_URL}/api/cso/${csoId}`, {
            withCredentials: true,
          });
          if (res.data?.logo)
            setCsoLogos((prev) => ({ ...prev, [csoId]: res.data.logo }));
        } catch (e) {}
      } catch (err) {
        console.error("Error fetching messages:", err);
      }
    },
    [API_URL, currentUser]
  );

  // ----- start polling for messages diffs + merge metadata -----
  const startMessagesPolling = useCallback(
    (csoId) => {
      if (pollingRef.current.messagesInterval) {
        clearInterval(pollingRef.current.messagesInterval);
        pollingRef.current.messagesInterval = null;
      }

      pollingRef.current.messagesInterval = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API_URL}/api/chat/cso/${csoId}`, {
            withCredentials: true,
          });
          if (!Array.isArray(data)) return;
          const ordered = sortMessagesAscending(data);

          const newOnes = [];
          const idMap = Object.fromEntries(ordered.map((m) => [m.id, m]));
          ordered.forEach((m) => {
            if (!messageTrackerRef.current.has(m.id)) {
              messageTrackerRef.current.add(m.id);
              newOnes.push(m);
            }
          });

          if (newOnes.length > 0) {
            // append new messages while preserving scroll
            updateMessagesPreserveScroll(
              (prev) => sortMessagesAscending([...prev, ...newOnes]),
              false
            );

            // update conversation preview (last_message + time) for this csoId
            const newest = newOnes[newOnes.length - 1];
            setConversations((prev) => {
              const updated = prev.map((c) =>
                c.cso_id === csoId
                  ? {
                      ...c,
                      last_message: newest.message,
                      last_message_time: newest.created_at,
                    }
                  : c
              );
              syncFiltered(updated);
              return updated;
            });

            // update unreadCounts for that convo
            setUnreadCounts((prev) => ({
              ...prev,
              [csoId]:
                (prev[csoId] || 0) +
                newOnes.filter((m) => m.sender_type === "cso").length,
            }));
          } else {
            // merge updates like read_at without disrupting scroll
            updateMessagesPreserveScroll(
              (prev) =>
                prev.map((m) => (idMap[m.id] ? { ...m, ...idMap[m.id] } : m)),
              false
            );
          }
        } catch (err) {
          // ignore transient
        }
      }, 2500);
    },
    [API_URL]
  );

  const stopMessagesPolling = () => {
    if (pollingRef.current.messagesInterval) {
      clearInterval(pollingRef.current.messagesInterval);
      pollingRef.current.messagesInterval = null;
    }
  };

  // ----- select chat handler -----
  const handleSelectChat = async (cso) => {
    if (!cso) return;
    setActiveChat(cso);
    // setTyping(false);
    stopMessagesPolling();

    await fetchMessagesOnce(cso.cso_id);

    // start polling this convo
    startMessagesPolling(cso.cso_id);
    setUnreadCounts((prev) => ({ ...prev, [cso.cso_id]: 0 }));

    // typing polling
    // if (pollingRef.current.typingInterval)
    //   clearInterval(pollingRef.current.typingInterval);
    // pollingRef.current.typingInterval = setInterval(async () => {
    //   try {
    //     const res = await axios.get(
    //       `${API_URL}/api/chat/typing/${cso.cso_id}`,
    //       { withCredentials: true }
    //     );
    //     setTyping(Boolean(res.data?.csoTyping));
    //   } catch (err) {}
    // }, 1500);
  };

  useEffect(() => {
    // return () => {
    //   if (pollingRef.current.typingInterval)
    //     clearInterval(pollingRef.current.typingInterval);
    // };
  }, [activeChat]);

  // ----- typing debounce / notify endpoint -----
  // const sendTyping = async (csoId, isTyping) => {
  //   try {
  //     await axios.post(
  //       `${API_URL}/api/chat/typing`,
  //       {
  //         csoId,
  //         isTyping,
  //         senderType: "staff",
  //         senderId: String(getUserId(currentUser) || ""),
  //       },
  //       { withCredentials: true }
  //     );
  //   } catch (err) {}
  // };

  // const handleTyping = () => {
  //   if (!activeChat) return;
  //   const isNowTyping = newMessage.trim().length > 0;
  //   if (isNowTyping) {
  //     if (!typingSentRef.current) {
  //       sendTyping(activeChat.cso_id, true);
  //       typingSentRef.current = true;
  //     }
  //     if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  //     typingTimeoutRef.current = setTimeout(() => {
  //       sendTyping(activeChat.cso_id, false);
  //       typingSentRef.current = false;
  //     }, 2500);
  //   } else {
  //     if (typingSentRef.current) {
  //       sendTyping(activeChat.cso_id, false);
  //       typingSentRef.current = false;
  //     }
  //     if (typingTimeoutRef.current) {
  //       clearTimeout(typingTimeoutRef.current);
  //       typingTimeoutRef.current = null;
  //     }
  //   }
  // };

  // ----- message send (optimistic) -----
  const handleSendMessage = async () => {
    // If currently editing a message, perform edit flow
    if (editingMessageId) {
      const trimmed = (newMessage || "").trim();
      if (!trimmed) return;
      // optimistic update + edited highlight
      updateMessagesPreserveScroll(
        (prev) =>
          prev.map((m) =>
            m.id === editingMessageId
              ? {
                  ...m,
                  message: trimmed,
                  is_edited: true,
                  wasRecentlyEdited: true,
                }
              : m
          ),
        false
      );
      const editingId = editingMessageId;
      setEditingMessageId(null);

      // clear input and reset height
      setNewMessage("");
      setTimeout(() => {
        if (messageInputRef.current) messageInputRef.current.style.height = "";
      }, 0);

      // remove glow after a short time
      setTimeout(() => {
        updateMessagesPreserveScroll(
          (prev) =>
            prev.map((m) =>
              m.id === editingId ? { ...m, wasRecentlyEdited: false } : m
            ),
          false
        );
      }, 1200);

      try {
        await axios.put(
          `${API_URL}/api/chat/${editingId}`,
          {
            editor_type: "staff",
            editor_id: getUserId(currentUser),
            message: trimmed,
          },
          { withCredentials: true }
        );
      } catch (err) {
        console.error("Failed to save edit", err);
        // refresh messages for robustness
        if (activeChat) await fetchMessagesOnce(activeChat.cso_id);
      }
      return;
    }

    // normal send new message flow
    if (
      (!newMessage.trim() && !file) ||
      !currentUser ||
      !activeChat ||
      isSending
    )
      return;
    setIsSending(true);
    const tempId = Date.now();
    const optimistic = {
      id: `temp-${tempId}`,
      tempId,
      sender_type: "staff",
      sender_id: getUserId(currentUser),
      sender_name: currentUser.name || currentUser.fullName || "Staff",
      recipient_id: activeChat.cso_id,
      message: newMessage,
      chat_file: file ? file.name : null,
      created_at: new Date().toISOString(),
      isOptimistic: true,
    };

    // Was user following the bottom? only then we auto-follow new messages
    const wasAtBottom = isUserAtBottom();

    // Add optimistic message but preserve scroll for scrolled-up users
    updateMessagesPreserveScroll((prev) => [...prev, optimistic], wasAtBottom);

    // update conversation preview immediately (optimistic)
    setConversations((prev) => {
      const updated = prev.map((c) =>
        c.cso_id === activeChat.cso_id
          ? {
              ...c,
              last_message: optimistic.message,
              last_message_time: optimistic.created_at,
            }
          : c
      );
      syncFiltered(updated);
      return updated;
    });

    const formData = new FormData();
    formData.append("sender_type", "staff");
    formData.append("sender_id", getUserId(currentUser));
    formData.append("recipient_id", activeChat.cso_id);
    formData.append("message", newMessage);
    formData.append("tempId", tempId);
    if (file) formData.append("chat_file", file);

    try {
      const res = await axios.post(`${API_URL}/api/chat`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success && res.data.data) {
        const created = res.data.data;
        // replace optimistic message with server message; preserve scroll
        updateMessagesPreserveScroll(
          (prev) => prev.map((m) => (m.id === `temp-${tempId}` ? created : m)),
          wasAtBottom
        );
        messageTrackerRef.current.add(created.id);

        // ensure conversation preview uses server timestamp/text (in case server normalized it)
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.cso_id === activeChat.cso_id
              ? {
                  ...c,
                  last_message: created.message,
                  last_message_time: created.created_at,
                }
              : c
          );
          syncFiltered(updated);
          return updated;
        });

        setNewMessage("");
        if (filePreviewUrl) {
          URL.revokeObjectURL(filePreviewUrl);
          setFilePreviewUrl(null);
        }
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => {
          if (messageInputRef.current)
            messageInputRef.current.style.height = "";
        }, 0);
      } else {
        // remove optimistic on failure
        updateMessagesPreserveScroll(
          (prev) => prev.filter((m) => m.id !== `temp-${tempId}`),
          false
        );
      }
    } catch (err) {
      console.error("Error sending message", err);
      updateMessagesPreserveScroll(
        (prev) => prev.filter((m) => m.id !== `temp-${tempId}`),
        false
      );
    } finally {
      setIsSending(false);
      if (activeChat) {
        // sendTyping(activeChat.cso_id, false);
        typingSentRef.current = false;
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      }
    }
  };

  // ----- file handling (single file only) -----
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // revoke previous preview
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    const url = URL.createObjectURL(f);
    setFile(f);
    setFilePreviewUrl(url);
  };

  const removeFile = () => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileDownload = (fileName) =>
    window.open(`${API_URL}/public/chat/${fileName}`, "_blank");

  // ----- edit / delete handlers (menu-triggered) -----
  const handleOpenMenu = (messageId, e) => {
    e.preventDefault();
    // position menu inside messages container
    const container = messagesContainerRef.current;
    const el = document.getElementById(`msg-${messageId}`);
    if (!container || !el) {
      setOpenMenuFor(messageId);
      setMenuPos({ top: 10, left: 10 });
      return;
    }
    const contRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - contRect.top + container.scrollTop; // menu relative to container + scroll
    // prefer to show menu to the right of message bubble; clamp to container width
    const left = Math.min(
      Math.max(elRect.left - contRect.left + 10, 8),
      container.clientWidth - 180
    );
    setMenuPos({ top, left });
    setOpenMenuFor(messageId);
  };

  // When Edit selected: put text into input and focus
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setNewMessage(message.message || "");
    setOpenMenuFor(null);
    // focus input after next tick
    setTimeout(() => {
      messageInputRef.current?.focus();
      autoResize();
    }, 50);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setNewMessage("");
    setTimeout(() => {
      if (messageInputRef.current) messageInputRef.current.style.height = "";
    }, 0);
  };

  const handleDeleteMessage = async (messageId) => {
    // if (!window.confirm("Delete this message?")) return;

    // Mark message as removing so CSS can animate it
    updateMessagesPreserveScroll(
      (prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, isRemoving: true } : m)),
      false
    );
    setOpenMenuFor(null);

    const ANIM_MS = 300; // must match CSS transition duration
    // after animation, remove optimistically and call API
    setTimeout(async () => {
      let removed = null;
      updateMessagesPreserveScroll((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx !== -1) {
          removed = prev[idx];
          const copy = prev.slice();
          copy.splice(idx, 1);
          return copy;
        }
        return prev;
      }, false);

      try {
        await axios.delete(`${API_URL}/api/chat/${messageId}`, {
          withCredentials: true,
          data: { deleter_type: "staff", deleter_id: getUserId(currentUser) },
        });
        console.log("Message deleted successfully");
        // success: nothing else to do
      } catch (err) {
        console.error("Failed to delete message", err);
        console.log("Message deleted failed");

        // restore (server might have rejected)
        if (removed)
          updateMessagesPreserveScroll(
            (prev) => sortMessagesAscending([...prev, removed]),
            false
          );
      }
    }, ANIM_MS);
  };

  // Close menu on outside click or on container scroll
  useEffect(() => {
    const onDocClick = (e) => {
      if (!openMenuFor) return;
      const menuEl = document.getElementById("message-context-menu");
      if (menuEl && menuEl.contains(e.target)) return;
      // if click inside a message element where menu is open, let message click handler handle it
      setOpenMenuFor(null);
    };
    const onScroll = () => {
      if (openMenuFor) setOpenMenuFor(null);
    };
    document.addEventListener("click", onDocClick);
    messagesContainerRef.current?.addEventListener("scroll", onScroll);
    return () => {
      document.removeEventListener("click", onDocClick);
      messagesContainerRef.current?.removeEventListener("scroll", onScroll);
    };
  }, [openMenuFor]);

  // ----- on messages change: initial load handling only -----
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      if (lastUnreadRef.current) {
        setTimeout(() => {
          const el = document.getElementById(`msg-${lastUnreadRef.current}`);
          if (el) el.scrollIntoView({ behavior: "auto", block: "center" });
          else scrollToBottom("auto");
        }, 0);
      } else {
        setTimeout(() => scrollToBottom("auto"), 0);
      }
    }
  }, [messages]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      stopMessagesPolling();
      if (pollingRef.current.convInterval)
        clearInterval(pollingRef.current.convInterval);
      if (pollingRef.current.typingInterval)
        clearInterval(pollingRef.current.typingInterval);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      try {
        const userId = getUserId(currentUser);
        if (userId) {
          const url = `${API_URL}/api/chat/presence`;
          const payload = JSON.stringify({
            senderType: "staff",
            senderId: String(userId),
            status: "offline",
          });
          const blob = new Blob([payload], { type: "application/json" });
          if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
          else
            axios
              .post(
                url,
                {
                  senderType: "staff",
                  senderId: String(userId),
                  status: "offline",
                },
                { withCredentials: true }
              )
              .catch(() => {});
        }
      } catch (e) {}
    };
  }, [API_URL, currentUser, filePreviewUrl]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // ----------------- New: day divider helpers & grouped rendering -----------------
  const sameDay = (a, b) => {
    const da = new Date(a);
    const db = new Date(b);
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  const formatDayLabel = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (sameDay(d, today)) return "Today";
    if (sameDay(d, yesterday)) return "Yesterday";
    // return localized date (day-month-year based on browser locale)
    return d.toLocaleDateString();
  };

  const formatLocalTime = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  const renderMessagesWithDividers = () => {
    const nodes = [];
    let lastDateKey = null;

    messages.forEach((message) => {
      const ts = message.created_at || message.createdAt || message.timestamp;
      const d = ts ? new Date(ts) : new Date();
      const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

      if (lastDateKey !== dateKey) {
        nodes.push(
          <div key={`day-${dateKey}`} className="day-divider">
            <span className="divider-text">{formatDayLabel(ts)}</span>
          </div>
        );
        lastDateKey = dateKey;
      }

      const staffMessageRead = Boolean(message.read_at || message.is_read);
      const isOwnStaffMessage =
        message.sender_type === "staff" &&
        String(message.sender_id) === String(getUserId(currentUser));

      // dynamic classes for animation states
      const cls = [
        "message",
        message.sender_type === "staff"
          ? isOwnStaffMessage
            ? "sent"
            : "staff-other"
          : "received",
        message.isRemoving ? "removing" : "",
        message.wasRecentlyEdited ? "edited-glow" : "",
      ]
        .join(" ")
        .trim();

      nodes.push(
        <div
          id={`msg-${message.id}`}
          key={message.id || message.tempId || Math.random()}
          className={cls}
          onContextMenu={(e) => handleOpenMenu(message.id, e)}
          onClick={(e) => {
            // open context menu for any message (menu will enable/disable actions based on ownership)
            handleOpenMenu(message.id, e);
          }}
        >
          <div className="message-sender">{message.sender_name}</div>

          <div className="message-bubble">
            {message.chat_file ? (
              <>
                {message.message && (
                  <div className="message-text">{message.message}</div>
                )}
                <div
                  className="message-file clickable"
                  onClick={() => handleFileDownload(message.chat_file)}
                >
                  <div className="file-icon">
                    <FaPaperclip />
                  </div>
                  <div className="file-details">
                    <div className="file-name">
                      {String(message.chat_file).split("/").pop()}
                    </div>
                    <div className="file-size">
                      {message.file_size
                        ? formatFileSize(message.file_size)
                        : "Unknown size"}
                    </div>
                  </div>
                  <div className="download-icon">
                    <FaFileDownload />
                  </div>
                </div>
              </>
            ) : (
              <div className="message-text">{message.message}</div>
            )}
            <div className="message-meta">
              <span className="timestamp">{formatLocalTime(ts)}</span>

              {message.sender_type === "staff" && (
                <span className="status-icon">
                  {staffMessageRead ? <FaCheckDouble /> : <FaCheck />}
                </span>
              )}

              {message.isOptimistic && (
                <span className="sending-indicator"> • sending...</span>
              )}
              {message.is_edited===1 && (
                <span className="edited-label"> (edited)</span>
              )}
            </div>
          </div>
        </div>
      );
    });

    return nodes;
  };

  // Placeholder action: open first chat
  const openFirstChat = () => {
    if (conversations && conversations.length > 0) {
      handleSelectChat(conversations[0]);
      setSidebarCollapsed(false);
    }
  };

  useImperativeHandle(ref, () => ({ openFirstChat }));

  return (
    <div
      className={`staff-chat-container ${
        sidebarCollapsed ? "sidebar-collapsed" : ""
      }`}
    >
      {/* Sidebar */}
      <div className="conversation-sidebar">
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {currentUser?.name?.charAt(0) || "S"}
            </div>
            <div>
              <div className="user-name">
                {currentUser?.name || "Staff User"}
              </div>
              <div className="user-status">Online</div>
            </div>
          </div>

          <div className="header-icons">
            <button
              className="collapse-btn"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              title={sidebarCollapsed ? "Expand" : "Collapse"}
            >
              {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
            </button>
            <FaEllipsisV />
          </div>
        </div>

        <div className="search-container">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="conversation-list">
          {filteredConversations.map((conversation) => (
            <div
              key={conversation.cso_id}
              className={`conversation ${
                activeChat?.cso_id === conversation.cso_id ? "active" : ""
              }`}
              onClick={() => handleSelectChat(conversation)}
            >
              <div className="avatar">
                <div className="avatar-img">
                  {csoLogos[conversation.cso_id] ? (
                    <img
                      src={
                        csoLogos[conversation.cso_id].startsWith("http")
                          ? csoLogos[conversation.cso_id]
                          : `${API_URL}/${csoLogos[conversation.cso_id]}`
                      }
                      alt={conversation.csoName}
                      style={{ width: 40, height: 40, borderRadius: 20 }}
                    />
                  ) : (
                    conversation.csoName?.charAt(0) || "C"
                  )}
                </div>
                {conversation.isOnline && <div className="status online"></div>}
              </div>
              <div className="conversation-details">
                <div className="conversation-header">
                  <div className="contact-name">{conversation.csoName}</div>
                  <div className="message-time">
                    {conversation.last_message_time
                      ? new Date(
                          conversation.last_message_time
                        ).toLocaleDateString()
                      : ""}
                  </div>
                </div>
                <div className="message-preview">
                  <div className="message-preview-text">
                    {conversation.last_message}
                  </div>
                  {unreadCounts[conversation.cso_id] > 0 && (
                    <div className="unread-count">
                      {unreadCounts[conversation.cso_id]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {(!filteredConversations || filteredConversations.length === 0) && (
            <div className="no-csos">No conversations</div>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="chat-area">
        {activeChat ? (
          <>
            <div className="chat-header">
              {isMobile && (
                <button className="back-button" onClick={toggleSidebar}>
                  <FiMenu />
                </button>
              )}
              <div className="chat-contact">
                <div className="avatar">
                  <div className="avatar-img">
                    {csoLogos[activeChat.cso_id] ? (
                      <img
                        src={
                          csoLogos[activeChat.cso_id].startsWith("http")
                            ? csoLogos[activeChat.cso_id]
                            : `${API_URL}/${csoLogos[activeChat.cso_id]}`
                        }
                        alt={activeChat.csoName}
                        style={{ width: 40, height: 40, borderRadius: 20 }}
                      />
                    ) : (
                      activeChat.csoName?.charAt(0) || "C"
                    )}
                  </div>
                  {activeChat.isOnline && <div className="status online"></div>}
                </div>
                <div>
                  <div className="chat-contact-name">{activeChat.csoName}</div>
                  <div className="chat-contact-status">
                    {typing
                      ? "typing..."
                      : activeChat.isOnline
                      ? "online"
                      : "offline"}
                  </div>
                </div>
              </div>
              <div className="chat-icons">
                <FaSearch />
                <FaEllipsisV />
              </div>
            </div>

            <div className="chat-messages" ref={messagesContainerRef}>
              {renderMessagesWithDividers()}
              <div ref={messagesEndRef} />
              {/* Context menu (rendered inside messages container so position is relative) */}
              {openMenuFor &&
                (() => {
                  const menuMsg = messages.find((m) => m.id === openMenuFor);
                  const canEdit =
                    menuMsg &&
                    String(menuMsg.sender_id) ===
                      String(getUserId(currentUser)) &&
                    menuMsg.sender_type === "staff";
                  return (
                    <div
                      id="message-context-menu"
                      className="message-context-menu"
                      style={{
                        position: "absolute",
                        top: menuPos.top,
                        left: menuPos.left,
                        zIndex: 50,
                      }}
                    >
                      <button
                        className={`context-item ${!canEdit ? "disabled" : ""}`}
                        onClick={() => {
                          if (canEdit) handleStartEdit(menuMsg);
                        }}
                        title={
                          canEdit
                            ? "Edit message"
                            : "You can only edit your own messages"
                        }
                      >
                        <FaEdit /> Edit
                      </button>

                      <button
                        className="context-item danger"
                        onClick={() => {
                          handleDeleteMessage(openMenuFor);
                        }}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  );
                })()}
            </div>

            {/* Input + File preview area */}
            <div className="chat-input-area">
              {/* File preview (Telegram-like) */}
              {file && (
                <div className="file-preview telegram">
                  <div className="thumb-wrap fade-in">
                    {file.type.startsWith("image/") && filePreviewUrl ? (
                      <img
                        src={filePreviewUrl}
                        alt={file.name}
                        className="file-thumb-img"
                      />
                    ) : (
                      <div className="file-thumb-icon">
                        <FaFileAlt />
                      </div>
                    )}
                    <button
                      className="file-remove-btn"
                      onClick={removeFile}
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="file-meta">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">
                      {file.size ? formatFileSize(file.size) : ""}
                    </div>
                  </div>
                </div>
              )}

              <div className="chat-input">
                <div className="input-actions">
                  <button
                    className="attach-btn"
                    onClick={() => fileInputRef.current.click()}
                    title="Attach file"
                  >
                    <FaPaperclip />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                  </button>
                  <button className="emoji-btn" title="Emoji">
                    <FaSmile />
                  </button>
                </div>

                <textarea
                  ref={messageInputRef}
                  className="message-input"
                  placeholder={
                    editingMessageId
                      ? "Editing message..."
                      : "Type a message..."
                  }
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // handleTyping();
                    autoResize();
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  rows={1}
                  disabled={isSending}
                />

                <div className="right-actions">
                  {editingMessageId && (
                    <button
                      className="cancel-edit"
                      onClick={handleCancelEdit}
                      title="Cancel edit"
                    >
                      Cancel
                    </button>
                  )}
                  {newMessage || file ? (
                    <button
                      className="send-button"
                      onClick={handleSendMessage}
                      disabled={isSending}
                    >
                      {isSending ? <div className="spinner"></div> : <MdSend />}
                    </button>
                  ) : (
                    <button
                      className="voice-button"
                      title="Voice (not implemented)"
                    >
                      <FaMicrophone />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-chat">
            <div className="empty-chat-content">
              <h3>Select a conversation</h3>
              <p>Start chatting by selecting a conversation from the list</p>
              <div className="empty-actions">
                <button className="open-first-chat" onClick={openFirstChat}>
                  Open first chat
                </button>
                <button
                  className="toggle-sidebar"
                  onClick={() => setSidebarCollapsed(false)}
                >
                  Show CSO list
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default StaffChat;
